import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { db } from "../firebase";
import {
  collection,
  addDoc,
  query,
  where,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { MessageCircle, Send, User } from "lucide-react";
import { ClipLoader } from "react-spinners";
import { useLang } from "../i18n/LangContext";

export default function Comments({ postSlug }) {
  const { t, formatDate: formatLocaleDate } = useLang();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const q = query(
      collection(db, "comments"),
      where("postSlug", "==", postSlug)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const commentsData = snapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .sort((a, b) => {
            // Ordenar por fecha descendente (más recientes primero)
            const dateA = a.createdAt?.toDate?.() || new Date(0);
            const dateB = b.createdAt?.toDate?.() || new Date(0);
            return dateB - dateA;
          });
        setComments(commentsData);
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching comments:", err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [postSlug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (!name.trim() || !message.trim()) {
      setError(t("comments.errorEmpty"));
      return;
    }

    if (message.trim().length < 3) {
      setError(t("comments.errorShort"));
      return;
    }

    setSubmitting(true);

    try {
      await addDoc(collection(db, "comments"), {
        postSlug,
        name: name.trim(),
        message: message.trim(),
        createdAt: serverTimestamp(),
      });

      setName("");
      setMessage("");
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error("Error adding comment:", err);
      setError(t("comments.errorSend"));
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return t("comments.justNow");
    const date = timestamp.toDate();
    return formatLocaleDate(date, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <section className="mt-12 pt-8 border-t border-slate-200 dark:border-slate-700">
      <div className="flex items-center gap-3 mb-6">
        <MessageCircle className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
        <h3 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100">
          {t("comments.title")}
        </h3>
        {comments.length > 0 && (
          <span className="px-2 py-1 text-sm font-medium rounded-full bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300">
            {comments.length}
          </span>
        )}
      </div>

      {/* Formulario */}
      <form
        onSubmit={handleSubmit}
        className="mb-8 p-5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700"
      >
        <div className="mb-4">
          <label
            htmlFor="name"
            className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
          >
            {t("comments.name")}
          </label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("comments.namePlaceholder")}
            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
            disabled={submitting}
          />
        </div>

        <div className="mb-4">
          <label
            htmlFor="message"
            className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
          >
            {t("comments.comment")}
          </label>
          <textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t("comments.commentPlaceholder")}
            rows={4}
            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors resize-none"
            disabled={submitting}
          />
        </div>

        {error && (
          <p className="text-red-600 dark:text-red-400 text-sm mb-4">{error}</p>
        )}

        {success && (
          <p className="text-green-600 dark:text-green-400 text-sm mb-4">
            {t("comments.success")}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <ClipLoader size={18} color="#ffffff" />
          ) : (
            <Send className="w-4 h-4" />
          )}
          {submitting ? t("comments.sending") : t("comments.submit")}
        </button>
      </form>

      {/* Lista de comentarios */}
      {loading ? (
        <div className="flex justify-center py-8">
          <ClipLoader size={32} color="#6366f1" />
        </div>
      ) : comments.length === 0 ? (
        <p className="text-center text-slate-500 dark:text-slate-400 py-8">
          {t("comments.empty")}
        </p>
      ) : (
        <div className="space-y-4">
          <AnimatePresence>
            {comments.map((comment, index) => (
              <motion.div
                key={comment.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ delay: index * 0.05, duration: 0.3 }}
                className="p-4 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center">
                    <User className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <span className="font-medium text-slate-900 dark:text-slate-100">
                      {comment.name}
                    </span>
                    <span className="text-sm text-slate-500 dark:text-slate-400 ml-3">
                      {formatDate(comment.createdAt)}
                    </span>
                  </div>
                </div>
                <p className="text-slate-700 dark:text-slate-300 pl-11 whitespace-pre-wrap">
                  {comment.message}
                </p>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </section>
  );
}
